import { Box, Stack } from '@mui/material';
import React from 'react';

import { Chip } from '@sbu/components.chip';
import { TagFields } from '@sbu/hooks.tags';

import {
  Field,
  FieldTypes,
  FilterTypes,
  ValueBasedFilterTypes,
} from '../types';
import { getFieldObject, getFilterLabels } from '../utils';

export interface FilterChipProps {
  fields: Field[];
  filter: FilterTypes;
  onEditField: (e: React.MouseEvent<HTMLElement>, filter: FilterTypes) => void;
  onEditOperator: (
    e: React.MouseEvent<HTMLElement>,
    filter: FilterTypes
  ) => void;
  onEditValue: (e: React.MouseEvent<HTMLElement>, filter: FilterTypes) => void;
  removeFilter: (e: React.MouseEvent<HTMLElement>, filter: FilterTypes) => void;
  deserializeKeyValue?: (value: string) => TagFields;
}

export const FilterChip = ({
  fields,
  filter,
  onEditField,
  onEditOperator,
  onEditValue,
  removeFilter,
  deserializeKeyValue,
}: FilterChipProps) => {
  let isRemovable = true;
  let field: FieldTypes | undefined;

  if ('prop' in filter) {
    field = getFieldObject(filter.prop, fields);
    isRemovable = !(field && field.removable === false);
  }

  const { fieldLabel, operatorLabel, valueLabel } = getFilterLabels(
    filter,
    fields
  );

  let fieldValueLabel = valueLabel;

  // Show the label instead of the key when predefined and a deserializer
  // function exists.
  const isKeyValue = field?.type === 'keyValue';
  if (isKeyValue && deserializeKeyValue) {
    const keyValue = deserializeKeyValue(
      (filter as ValueBasedFilterTypes).value as string
    );
    fieldValueLabel =
      keyValue.key && keyValue.label
        ? `${keyValue.label}: ${
            keyValue.valueLabel
              ? `${keyValue.value} - ${keyValue.valueLabel}`
              : keyValue.value
          }`
        : keyValue.value;
  }

  const LabelButton = ({
    label,
    onClick,
  }: {
    label: string;
    onClick?: (e: React.MouseEvent<HTMLElement>, filter: FilterTypes) => void;
  }) => (
    <Box
      component='span'
      onClick={onClick ? (e) => onClick(e, filter) : undefined}
      whiteSpace='nowrap'
      sx={{
        ':hover': {
          textDecoration: 'underline',
        },
        cursor: 'pointer',
      }}
      role='button'
    >
      {label}
    </Box>
  );

  const chipLabel = (
    <Box
      display='flex'
      alignItems='center'
      gap={0.5}
      sx={{ overflow: 'hidden' }}
    >
      <LabelButton label={fieldLabel} onClick={onEditField} />
      {operatorLabel && (
        <LabelButton label={operatorLabel} onClick={onEditOperator} />
      )}
      <Box
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          minWidth: 0,
        }}
      >
        <LabelButton label={fieldValueLabel ?? 'Empty'} onClick={onEditValue} />
      </Box>
    </Box>
  );
  const removeFilterLabel = `Remove filter for ${fieldLabel}${
    operatorLabel ? ` ${operatorLabel}` : ''
  }${fieldValueLabel ? ` ${fieldValueLabel}` : ''}`;
  const chipTitle = `${fieldLabel}${
    operatorLabel ? ` ${operatorLabel}` : ''
  }${fieldValueLabel ? ` ${fieldValueLabel}` : ''}`;

  return (
    <Stack
      data-sumo-test='filter-chip'
      justifyContent='flex-start'
      ml={1.25}
      gap={0.5}
    >
      <Chip
        DeleteButtonProps={{
          'aria-label': removeFilterLabel,
          'data-sumo-test': 'filter-chip-close-btn',
        }}
        label={chipLabel}
        onDelete={isRemovable ? (e) => removeFilter(e, filter) : undefined}
        StackProps={{
          'data-sumo-test': 'filter-chip-value',
          width: '100%',
        }}
        variant='outlined'
        title={chipTitle}
      />
    </Stack>
  );
};

export default FilterChip;
